import { useResetFinalPriceOnChange } from "@/services/backend/rlt/pricing/lib/use-reset-final-price-on-change";
import { NavLink, useParams } from "react-router";
import {
  useRltConfirmationShowQuery,
  useRltConfirmationUpdateStateMutation,
} from "@/services/backend/rlt/confirmations/api/confirmation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Confirmation } from "@/services/backend/rlt/confirmations/types/confirmation";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { DeleteConfirmationDialog } from "@/routes/processes/[processId]/rlt/confirmations/[confirmationId]/_components/delete-confirmation-dialog";
import { ConfirmationPdf } from "@/routes/processes/[processId]/rlt/confirmations/[confirmationId]/_components/confirmation-pdf";
import { DuplicateConfirmationButton } from "@/routes/processes/[processId]/rlt/confirmations/[confirmationId]/_components/duplicate-confirmation-button";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { RltBreadcrumbs } from "@/routes/processes/[processId]/rlt/_components/rlt-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function ConfirmationRoute() {
  const { confirmationId } = useParams();

  const {
    data: confirmation,
    isLoading,
    error,
  } = useRltConfirmationShowQuery({
    id: confirmationId!,
  });

  const breadcrumbs = confirmation && (
    <RltBreadcrumbs
      customerId={confirmation.customerId}
      processId={confirmation.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink
            to={`/processes/rlt/${confirmation.processId!}/confirmations`}
          >
            {t("Auftragsbestätigungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={confirmation.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </RltBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {confirmation && <ConfirmationPage confirmation={confirmation} />}
    </ProcessPageMainTemplate>
  );
}

function ConfirmationPage(props: { confirmation: Confirmation }) {
  const { confirmation } = props;

  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <Page confirmation={confirmation} />
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

function Page({ confirmation }: { confirmation: Confirmation }) {
  useResetFinalPriceOnChange(confirmation.pricingId);

  return (
    <DocumentPage>
      <DocumentStateAlert document={confirmation} />
      <DocumentPageHeader>
        <TabNavigation />
        <DocumentPageActions>
          {isImmutable(confirmation) ? (
            <DuplicateConfirmationButton
              processId={confirmation.processId}
              confirmationId={confirmation.id}
            />
          ) : (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteConfirmationDialog confirmationId={confirmation.id} />
            </>
          )}
          <DocumentStateDropdown
            document={confirmation}
            mutation={useRltConfirmationUpdateStateMutation}
          />
          <ConfirmationPdf confirmation={confirmation} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

function TabNavigation() {
  // The field names of each tab: the keys of confirmation.ReadyForCustomer
  // and of the validation errors of the fields shown on the tab.
  const links = [
    {
      to: "data",
      fieldName: [
        "referenceNumber",
        "date",
        "orderId",
        "orderDate",
        "orderReference",
        "offerId",
        "offerDate",
        "offerReference",
        "internalComment",
        "treatmentPrice",
        "incidentalCost",
        "extras",
        "areas",
      ],
      label: t("Bestätigung"),
    },
    {
      to: "treatment",
      label: t("Leistungen"),
    },
    {
      to: "calculation",
      label: t("Kalkulation"),
    },
    {
      to: "addresses-and-contacts",
      fieldName: [
        "postalAddress",
        "serviceAddress",
        "serviceContact",
        "representative",
      ],
      label: t("Adressen & Kontakte"),
    },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          fieldName={link.fieldName}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
